import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useUser } from "$global-providers/user-provider/provider";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { useCan } from "../../_lib/rbac/use-can";
import { MATCH_NOTHING } from "../../_lib/rxdb-selectors";

export type PendingEncounters = {
  assignedTo: (reviewerId: string) => EpocusEncounter[];
  pool: EpocusEncounter[];
  loading: boolean;
};

export const usePendingEncounters = (): PendingEncounters => {
  const canViewQaWorkload = useCan("viewQaWorkload");

  const query = useCallback(
    (collection: RxCollection<EpocusEncounter>) =>
      collection.find({
        selector: canViewQaWorkload
          ? { status: { $eq: "pending_review" } }
          : MATCH_NOTHING,
        sort: [{ date: "asc" }],
      }),
    [canViewQaWorkload],
  );

  const { result: pending, isFetching } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    query,
  );

  return useMemo(
    () => ({
      assignedTo: (reviewerId: string) =>
        pending.filter(
          (encounter) => encounter.assignedReviewerId === reviewerId,
        ),
      pool: pending.filter(
        (encounter) => (encounter.assignedReviewerId ?? null) === null,
      ),
      loading: isFetching,
    }),
    [pending, isFetching],
  );
};

/**
 * There is no assign mutation: assignment is two fields on a replicated
 * encounter, so an admin writes them the same way a reviewer claims from the
 * pool and the change reaches every device through normal sync. `assignedAt`
 * is authored here, on the device, because the server stores what it is given
 * rather than stamping its own time.
 */
export const useAssignReviewer = (): ((
  encounterId: string,
  reviewerId: string,
) => Promise<void>) => {
  const { getCurrentTime } = useServerTime();
  const { id: userId } = useUser();
  const collection = useRxCollectionEpocusEncounter();

  return useCallback(
    async (encounterId: string, reviewerId: string) => {
      if (!collection) return;
      const doc = await collection.findOne(encounterId).exec();
      if (!doc) return;

      const now = getCurrentTime().toISOString();
      await doc.incrementalModify((data) => ({
        ...data,
        assignedReviewerId: reviewerId,
        assignedAt: now,
        meta: { ...data.meta, updatedAt: now, updatedByUserId: userId },
      }));
    },
    [collection, getCurrentTime, userId],
  );
};
